/* eslint-disable react/prop-types */
import React from 'react';
import { EnvironmentOutlined, FileSearchOutlined } from '@ant-design/icons';
import { Space, Tag, Tooltip, Typography } from 'antd';

import moment from 'moment';
import styles from './MerchantHeader.less';

const { Text } = Typography;

const formatBindText = (tenantBind) => {
  if (Array.isArray(tenantBind)) {
    return (
      tenantBind
        .map((item) =>
          typeof item === 'object'
            ? item.refName || item.name || item.bindTypeName
            : item,
        )
        .filter(Boolean)
        .join('、') || '--'
    );
  }
  if (tenantBind && typeof tenantBind === 'object') {
    return tenantBind.refName || tenantBind.name || tenantBind.bindTypeName || '--';
  }
  return tenantBind || '--';
};

/**
 * MerchantHeader —— 商家档案顶部信息栏（固定高度，不随内容滚动）
 * ------------------------------------------------------------------
 * 框架要求：商家名称 / 企业认证标 / 创建时间 / 归属关系（查看和分配） /
 * 企业信息（查看） / 店铺介绍，右侧为操作区（LITTA POWER、查看大人物）。
 *
 * 这一层只做「展示 + 定位入口」。归属关系与企业信息的实际弹窗逻辑仍在
 * components/merchantInfo 里（本轮不重写业务组件），点击入口通过
 * onLocate 让父级切到「商家档案 → 概览」对应区块。
 *
 * @param tenantInfo  商家详情（athenaGetTenantInfo 返回）
 * @param baseTags    基础标签（页面壳 getBaseTag 返回，与「商家档案 → 商家信息 → 基础标签」同一份数据）
 * @param actions     右侧操作区节点（一般传 <HeaderActions/>）
 * @param onLocate     回调：入参为 sectionKey，取值为 bind 或 enterprise
 */
const MerchantHeader = (props) => {
  const { tenantInfo = {}, baseTags = [], actions, onLocate } = props;

  const { name, ctime, location, enterpriseVerificationStatus, tenantBind } =
    tenantInfo;

  /* 企业认证：enterpriseVerificationStatus 语义（取自 merchantInfo/index.jsx:425-436）：
     2=待审核、3=审核通过、4=审核失败、其它=未提交。
     只有 3（审核通过）才展示「企业已认证」。
     ⚠ 此处曾误写成 === '2'（待审核），导致已认证商家（实测 status=3）永远不显示——
     注释不能写「与某处一致」就算了，值要对得上。 */
  const verified = String(enterpriseVerificationStatus) === '3';

  // 与「商家信息」中的基础信息保持同一展示口径，直接展示接口返回的归属内容。
  const bindText = formatBindText(tenantBind);

  return (
    <div className={styles.header} data-pg-sticky-header>
      <div className={styles.topRow}>
        <div className={styles.main}>
          <div className={styles.titleRow}>
            <h2 className={styles.name}>{name || '-'}</h2>
            {verified && (
              <Tag color="blue" className={styles.tag}>
                企业已认证
              </Tag>
            )}
            {location && (
              <Tooltip title={location}>
                <Text className={styles.location}>
                  <EnvironmentOutlined />
                  {location}
                </Text>
              </Tooltip>
            )}
          </div>

          <Space className={styles.metaRow} size={20} wrap>
            <Text className={styles.meta}>
              <span className={styles.metaLabel}>商家ID</span>
              {tenantInfo.tenantId || '-'}
            </Text>
            <Text className={styles.meta}>
              <span className={styles.metaLabel}>创建时间</span>
              {ctime ? moment(ctime).format('YYYY-MM-DD HH:mm') : '-'}
            </Text>

            <Text className={styles.meta}>
              <span className={styles.metaLabel}>归属关系</span>
              <Tooltip title={bindText}>
                <span className={styles.bindText}>{bindText}</span>
              </Tooltip>
              <FileSearchOutlined
                className={styles.bindListIcon}
                role="button"
                tabIndex={0}
                aria-label="查看归属列表"
                onClick={() => onLocate && onLocate('bindList')}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onLocate && onLocate('bindList');
                  }
                }}
              />
              <a
                className={styles.assignLink}
                onClick={() => onLocate && onLocate('bind')}
              >
                分配
              </a>
            </Text>
          </Space>
          {/* 图2 反馈：企业信息与店铺介绍在「商家档案 → 商家信息 → 基础信息」里
              已有完整展示，顶部信息栏不再重复；删掉后信息栏更矮、下方内容上移 */}
        </div>

        {actions && <div className={styles.actions}>{actions}</div>}
      </div>

      {/* 标签行：复用页面壳 getBaseTag 的数据（原样展示在「商家信息 → 基础标签」） */}
      <div className={styles.tagRow}>
        {baseTags.length ? (
          <div className={styles.tagList}>
            {baseTags.map((t, i) => (
              <Tag key={i} color={t.ifGreen ? 'green' : undefined}>
                {t.name}
              </Tag>
            ))}
          </div>
        ) : (
          <span className={styles.tagEmpty}>暂无基础标签</span>
        )}
      </div>
    </div>
  );
};

export default MerchantHeader;
